import { useCallback, useEffect, useState } from "react";
import CreateQuizPage from "./CreateQuizPage";
import Pagination, { QUIZ_LIST_PAGE_SIZE } from "../components/Pagination";
import { apiUrl } from "../utils/api";
import "../style/Quiz.css";

const ADMIN_API_URL = process.env.REACT_APP_ADMIN_API_URL || apiUrl("admin.php");
const QUIZZES_API_URL = process.env.REACT_APP_QUIZ_API_URL || apiUrl("quizzes.php");

async function readJsonResponse(response) {
    const result = await response.json();
    if (!response.ok) {
        throw new Error(result.error || `Admin API returned HTTP ${response.status}`);
    }
    return result;
}

export default function AdminPanelPage({ text }) {
    const [data, setData] = useState({
        users: [],
        quizzes: [],
        schoolClassrooms: [],
        reports: [],
        siteContent: null
    });
    const [section, setSection] = useState("overview");
    const [editor, setEditor] = useState(null);
    const [classroomName, setClassroomName] = useState("");
    const [subjectName, setSubjectName] = useState("");
    const [subjectClassroomId, setSubjectClassroomId] = useState("");
    const [userPasswords, setUserPasswords] = useState({});
    const [visibleUserPasswords, setVisibleUserPasswords] = useState({});
    const [notice, setNotice] = useState("");
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [reloadVersion, setReloadVersion] = useState(0);
    const [quizPage, setQuizPage] = useState(1);
    const [siteContentDraft, setSiteContentDraft] = useState(null);
    const [siteContentDirty, setSiteContentDirty] = useState(false);
    const [isSavingSiteContent, setIsSavingSiteContent] = useState(false);

    useEffect(() => {
        if (data.siteContent && !siteContentDirty) {
            setSiteContentDraft(data.siteContent);
        }
    }, [data.siteContent, siteContentDirty]);

    const formatActivityTime = (timestamp) => (
        timestamp
            ? new Date(timestamp.replace(" ", "T")).toLocaleString()
            : text.never
    );

    const refresh = useCallback(async (signal, showLoading = true) => {
        if (showLoading) {
            setIsLoading(true);
        }
        setError("");
        try {
            const response = await fetch(ADMIN_API_URL, { signal });
            setData(await readJsonResponse(response));
        } catch (requestError) {
            if (requestError.name !== "AbortError") {
                console.error("Failed to load admin panel:", requestError);
                setError(text.adminErrors[requestError.message] || text.adminLoadError);
            }
        } finally {
            if (!signal.aborted) {
                setIsLoading(false);
            }
        }
    }, [text]);

    useEffect(() => {
        const controller = new AbortController();
        refresh(controller.signal);
        const intervalId = window.setInterval(
            () => refresh(controller.signal, false),
            60_000
        );
        return () => {
            window.clearInterval(intervalId);
            controller.abort();
        };
    }, [refresh, reloadVersion]);

    const sendAction = async (payload) => {
        setError("");
        setNotice("");
        try {
            const response = await fetch(ADMIN_API_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });
            await readJsonResponse(response);
            setNotice(text.adminActionSuccess);
            setReloadVersion((version) => version + 1);
            return true;
        } catch (requestError) {
            console.error("Admin action failed:", requestError);
            setError(text.adminErrors[requestError.message] || text.adminActionError);
            return false;
        }
    };

    const addClassroom = async (event) => {
        event.preventDefault();
        if (await sendAction({ action: "add_school_classroom", name: classroomName })) {
            setClassroomName("");
        }
    };

    const addSubject = async (event) => {
        event.preventDefault();
        if (await sendAction({
            action: "add_school_subject",
            name: subjectName,
            classroomId: Number(subjectClassroomId)
        })) {
            setSubjectName("");
        }
    };

    const deleteSchoolClassroom = (classroom) => {
        if (window.confirm(`${text.confirmDeleteSchoolClassroom} "${classroom.name}"?`)) {
            sendAction({ action: "delete_school_classroom", id: classroom.id });
        }
    };

    const deleteSchoolSubject = (subject) => {
        if (window.confirm(`${text.confirmDeleteSubject} "${subject.name}"?`)) {
            sendAction({ action: "delete_school_subject", id: subject.id });
        }
    };

    const openQuizEditor = async (quizId) => {
        setError("");
        try {
            const response = await fetch(QUIZZES_API_URL);
            const quizzes = await readJsonResponse(response);
            const quiz = quizzes.find((item) => Number(item.id) === Number(quizId));
            if (!quiz) {
                throw new Error("quiz_not_found");
            }
            setEditor(quiz);
        } catch (requestError) {
            console.error("Failed to load quiz for editing:", requestError);
            setError(text.adminErrors[requestError.message] || text.adminActionError);
        }
    };

    const deleteQuiz = (quiz) => {
        if (window.confirm(`${text.confirmDeleteQuiz} "${quiz.title}"?`)) {
            sendAction({ action: "delete_quiz", id: quiz.id });
        }
    };

    const deleteUser = (user) => {
        if (window.confirm(`${text.confirmDeleteUser} "${user.name}"?`)) {
            sendAction({ action: "delete_user", id: user.id });
        }
    };

    const resetUserPassword = async (event, userId) => {
        event.preventDefault();
        const password = userPasswords[userId] || "";
        if (await sendAction({ action: "set_user_password", id: userId, password })) {
            setUserPasswords((current) => ({ ...current, [userId]: "" }));
        }
    };

    const saveSiteContent = async (event) => {
        event.preventDefault();
        if (!siteContentDraft) {
            return;
        }
        setIsSavingSiteContent(true);
        const saved = await sendAction({
            action: "update_site_content",
            ...siteContentDraft
        });
        if (saved) {
            setSiteContentDirty(false);
        }
        setIsSavingSiteContent(false);
    };

    const quizPageCount = Math.ceil(data.quizzes.length / QUIZ_LIST_PAGE_SIZE);
    const currentQuizPage = Math.min(quizPage, Math.max(1, quizPageCount));
    const paginatedQuizzes = data.quizzes.slice(
        (currentQuizPage - 1) * QUIZ_LIST_PAGE_SIZE,
        currentQuizPage * QUIZ_LIST_PAGE_SIZE
    );

    if (editor !== null) {
        return (
            <CreateQuizPage
                text={text}
                schoolClassrooms={data.schoolClassrooms}
                initialQuiz={editor}
                onBack={() => setEditor(null)}
                onCreated={() => {
                    setEditor(null);
                    setReloadVersion((version) => version + 1);
                    setNotice(text.adminActionSuccess);
                }}
            />
        );
    }

    return (
        <main className="admin-page">
            <header className="admin-header">
                <div>
                    <span className="quiz-label">{text.adminLabel}</span>
                    <h1>{text.adminPanelTitle}</h1>
                    <p>{text.adminPanelDescription}</p>
                </div>
                <button
                    className="quiz-btn"
                    type="button"
                    onClick={() => setEditor(false)}
                >
                    {text.createQuizButton}
                </button>
            </header>

            <nav className="admin-tabs" aria-label={text.adminSections}>
                {["overview", "quizzes", "classrooms", "users", "reports", "siteContent"].map((tab) => (
                    <button
                        key={tab}
                        type="button"
                        className={section === tab ? "admin-tab active" : "admin-tab"}
                        onClick={() => setSection(tab)}
                    >
                        {text.adminTabs[tab]}
                    </button>
                ))}
            </nav>

            {notice && <p className="admin-notice" role="status">{notice}</p>}
            {error && <p className="auth-error admin-error" role="alert">{error}</p>}

            {isLoading ? (
                <p className="quiz-empty-state" role="status">{text.adminLoading}</p>
            ) : section === "overview" ? (
                <section className="admin-stats">
                    <article className="admin-stat-card">
                        <span>{text.adminQuizCount}</span>
                        <strong>{data.quizzes.length}</strong>
                    </article>
                    <article className="admin-stat-card">
                        <span>{text.adminUserCount}</span>
                        <strong>{data.users.length}</strong>
                    </article>
                </section>
            ) : section === "siteContent" ? (
                <section className="admin-section">
                    <h2>{text.siteContentTitle}</h2>
                    <p>{text.siteContentDescription}</p>
                    {siteContentDraft && (
                        <form className="site-content-form" onSubmit={saveSiteContent}>
                            <label className="auth-field">
                                <span>{text.aboutContentEnglishLabel}</span>
                                <textarea
                                    value={siteContentDraft.about_en}
                                    maxLength={5000}
                                    required
                                    onChange={(event) => {
                                        setSiteContentDraft((current) => ({
                                            ...current,
                                            about_en: event.target.value
                                        }));
                                        setSiteContentDirty(true);
                                    }}
                                />
                            </label>
                            <label className="auth-field">
                                <span>{text.aboutContentArabicLabel}</span>
                                <textarea
                                    dir="rtl"
                                    value={siteContentDraft.about_ar}
                                    maxLength={5000}
                                    required
                                    onChange={(event) => {
                                        setSiteContentDraft((current) => ({
                                            ...current,
                                            about_ar: event.target.value
                                        }));
                                        setSiteContentDirty(true);
                                    }}
                                />
                            </label>
                            <label className="auth-field">
                                <span>{text.supportContentEnglishLabel}</span>
                                <textarea
                                    value={siteContentDraft.support_en}
                                    maxLength={5000}
                                    required
                                    onChange={(event) => {
                                        setSiteContentDraft((current) => ({
                                            ...current,
                                            support_en: event.target.value
                                        }));
                                        setSiteContentDirty(true);
                                    }}
                                />
                            </label>
                            <label className="auth-field">
                                <span>{text.supportContentArabicLabel}</span>
                                <textarea
                                    dir="rtl"
                                    value={siteContentDraft.support_ar}
                                    maxLength={5000}
                                    required
                                    onChange={(event) => {
                                        setSiteContentDraft((current) => ({
                                            ...current,
                                            support_ar: event.target.value
                                        }));
                                        setSiteContentDirty(true);
                                    }}
                                />
                            </label>
                            <button
                                className="quiz-btn"
                                type="submit"
                                disabled={isSavingSiteContent}
                            >
                                {isSavingSiteContent
                                    ? text.savingSiteContent
                                    : text.saveSiteContent}
                            </button>
                        </form>
                    )}
                </section>
            ) : section === "quizzes" ? (
                <section className="admin-section">
                    <h2>{text.adminQuizzesTitle}</h2>
                    {data.quizzes.length ? (
                        <div className="admin-list">
                            {paginatedQuizzes.map((quiz) => (
                                <article className="admin-list-item" key={quiz.id}>
                                    <div>
                                        <div className="admin-quiz-title">
                                            <h3>{quiz.title}</h3>
                                            {quiz.isNew && (
                                                <span className="quiz-new-badge">{text.newQuiz}</span>
                                            )}
                                        </div>
                                        <p><strong>{text.schoolClassroom}:</strong> {quiz.schoolTrack}</p>
                                        <p><strong>{text.subjectLabel}:</strong> {quiz.category}</p>
                                        <p>{quiz.questionCount} {text.questions}</p>
                                    </div>
                                    <div className="admin-item-actions">
                                        <button
                                            className="quiz-secondary-btn"
                                            type="button"
                                            onClick={() => openQuizEditor(quiz.id)}
                                        >
                                            {text.editQuiz}
                                        </button>
                                        <button
                                            className="admin-danger-btn"
                                            type="button"
                                            onClick={() => deleteQuiz(quiz)}
                                        >
                                            {text.deleteButton}
                                        </button>
                                    </div>
                                </article>
                            ))}
                        </div>
                    ) : <p className="quiz-empty-state">{text.noAdminQuizzes}</p>}
                    <Pagination
                        page={currentQuizPage}
                        pageSize={QUIZ_LIST_PAGE_SIZE}
                        totalItems={data.quizzes.length}
                        text={text}
                        onPageChange={setQuizPage}
                    />
                </section>
            ) : section === "classrooms" ? (
                <section className="admin-section">
                    <h2>{text.manageSchoolClassrooms}</h2>
                    <form className="admin-category-form" onSubmit={addClassroom}>
                        <label className="auth-field">
                            <span>{text.newSchoolClassroom}</span>
                            <input
                                value={classroomName}
                                onChange={(event) => setClassroomName(event.target.value)}
                                maxLength={100}
                                required
                            />
                        </label>
                        <button className="quiz-btn" type="submit">
                            {text.addSchoolClassroom}
                        </button>
                    </form>
                    <form className="admin-category-form" onSubmit={addSubject}>
                        <label className="auth-field">
                            <span>{text.schoolClassroom}</span>
                            <select
                                value={subjectClassroomId}
                                onChange={(event) => setSubjectClassroomId(event.target.value)}
                                required
                            >
                                <option value="" disabled>{text.selectClassroom}</option>
                                {data.schoolClassrooms.map((classroom) => (
                                    <option key={classroom.id} value={classroom.id}>
                                        {classroom.name === "General"
                                            ? text.generalClassroom
                                            : classroom.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="auth-field">
                            <span>{text.newSubject}</span>
                            <input
                                value={subjectName}
                                onChange={(event) => setSubjectName(event.target.value)}
                                maxLength={100}
                                required
                            />
                        </label>
                        <button
                            className="quiz-btn"
                            type="submit"
                            disabled={!subjectClassroomId}
                        >
                            {text.addSubject}
                        </button>
                    </form>
                    <div className="admin-list">
                        {data.schoolClassrooms.map((classroom) => (
                            <article className="admin-list-item" key={classroom.id}>
                                <div>
                                    <h3>{classroom.name === "General"
                                        ? text.generalClassroom
                                        : classroom.name}</h3>
                                    {classroom.subjects.length ? (
                                        <ul className="admin-subject-list">
                                            {classroom.subjects.map((subject) => (
                                                <li key={subject.id}>
                                                    <span>{subject.name}</span>
                                                    <button
                                                        className="admin-danger-btn"
                                                        type="button"
                                                        onClick={() => deleteSchoolSubject(subject)}
                                                    >
                                                        {text.deleteButton}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : <p>{text.noSubjects}</p>}
                                </div>
                                {classroom.name !== "General" && (
                                    <button
                                        className="admin-danger-btn"
                                        type="button"
                                        onClick={() => deleteSchoolClassroom(classroom)}
                                    >
                                        {text.deleteSchoolClassroom}
                                    </button>
                                )}
                            </article>
                        ))}
                    </div>
                </section>
            ) : section === "users" ? (
                <section className="admin-section">
                    <h2>{text.adminUsersTitle}</h2>
                    <p className="admin-password-reset-hint">{text.adminPasswordResetHint}</p>
                    <div className="admin-list">
                        {data.users.map((user) => (
                            <article className="admin-list-item" key={user.id}>
                                <div>
                                    <h3>{user.name}</h3>
                                    <p>{user.phone}</p>
                                    <span className={
                                        user.isOnline
                                            ? "user-presence-badge online"
                                            : "user-presence-badge offline"
                                    }>
                                        {user.isOnline ? text.userOnline : text.userOffline}
                                    </span>
                                    <p className="user-activity-time">
                                        {text.lastLogin}: {formatActivityTime(user.lastLoginAt)}
                                    </p>
                                    <p className="user-activity-time">
                                        {text.lastLogout}: {formatActivityTime(user.lastLogoutAt)}
                                    </p>
                                    <span className={user.isAdmin ? "admin-role-badge" : "user-role-badge"}>
                                        {user.isAdmin ? text.adminRole : text.userRole}
                                    </span>
                                    {user.passwordResetRequestedAt && (
                                        <p className="admin-password-reset-request" role="status">
                                            <strong>{text.passwordResetRequested}</strong>
                                            <span>
                                                {text.failedLoginAttemptsLabel}:{" "}
                                                {user.failedLoginAttempts}
                                            </span>
                                            <time dateTime={user.passwordResetRequestedAt.replace(" ", "T")}>
                                                {formatActivityTime(user.passwordResetRequestedAt)}
                                            </time>
                                        </p>
                                    )}
                                    {user.canCreateQuizzes && (
                                        <span className="user-role-badge">{text.quizAuthorRole}</span>
                                    )}
                                </div>
                                <div className="admin-user-controls">
                                    <form
                                        className="admin-password-reset-form"
                                        onSubmit={(event) => resetUserPassword(event, user.id)}
                                    >
                                        <label htmlFor={`admin-user-password-${user.id}`}>
                                            {text.adminNewPasswordLabel}
                                        </label>
                                        <div className="admin-password-input-wrap">
                                            <input
                                                id={`admin-user-password-${user.id}`}
                                                type={visibleUserPasswords[user.id] ? "text" : "password"}
                                                autoComplete="new-password"
                                                minLength={8}
                                                maxLength={4096}
                                                required
                                                value={userPasswords[user.id] || ""}
                                                onChange={(event) => setUserPasswords((current) => ({
                                                    ...current,
                                                    [user.id]: event.target.value
                                                }))}
                                            />
                                            <button
                                                className="admin-password-visibility"
                                                type="button"
                                                aria-label={visibleUserPasswords[user.id]
                                                    ? text.hidePassword
                                                    : text.showPassword}
                                                aria-pressed={Boolean(visibleUserPasswords[user.id])}
                                                onClick={() => setVisibleUserPasswords((current) => ({
                                                    ...current,
                                                    [user.id]: !current[user.id]
                                                }))}
                                            >
                                                {visibleUserPasswords[user.id]
                                                    ? text.hidePassword
                                                    : text.showPassword}
                                            </button>
                                        </div>
                                        <button className="quiz-secondary-btn" type="submit">
                                            {text.resetUserPassword}
                                        </button>
                                    </form>
                                    <div className="admin-item-actions">
                                        <button
                                            className="quiz-secondary-btn"
                                            type="button"
                                            onClick={() => sendAction({
                                                action: "set_admin",
                                                id: user.id,
                                                isAdmin: !user.isAdmin
                                            })}
                                        >
                                            {user.isAdmin ? text.removeAdmin : text.makeAdmin}
                                        </button>
                                        <button
                                            className="quiz-secondary-btn"
                                            type="button"
                                            onClick={() => sendAction({
                                                action: "set_quiz_author",
                                                id: user.id,
                                                canCreateQuizzes: !user.canCreateQuizzes
                                            })}
                                        >
                                            {user.canCreateQuizzes
                                                ? text.removeQuizAuthor
                                                : text.makeQuizAuthor}
                                        </button>
                                        <button
                                            className="admin-danger-btn"
                                            type="button"
                                            onClick={() => deleteUser(user)}
                                        >
                                            {text.deleteButton}
                                        </button>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                </section>
            ) : (
                <section className="admin-section">
                    <h2>{text.questionReportsTitle}</h2>
                    {data.reports.length === 0 ? (
                        <p className="quiz-empty-state">{text.noQuestionReports}</p>
                    ) : (
                        <div className="admin-list">
                            {data.reports.map((report) => (
                                <article className="admin-list-item" key={report.id}>
                                    <div className="question-report-details">
                                        <h3>{report.quizTitle}</h3>
                                        <p>{report.questionText}</p>
                                        <p>
                                            {text.reportedBy} {report.reporterName}
                                            {" · "}
                                            <time dateTime={report.createdAt}>
                                                {new Date(report.createdAt.replace(" ", "T"))
                                                    .toLocaleString()}
                                            </time>
                                        </p>
                                        <span className={
                                            report.status === "open"
                                                ? "question-report-status open"
                                                : "question-report-status"
                                        }>
                                            {report.status === "open"
                                                ? text.reportOpen
                                                : text.reportResolved}
                                        </span>
                                    </div>
                                    <div className="admin-item-actions">
                                        <button
                                            className="quiz-secondary-btn"
                                            type="button"
                                        onClick={() => openQuizEditor(report.quizId)}
                                    >
                                        {text.editReportedQuestion}
                                    </button>
                                    {report.status === "open" && (
                                        <button
                                            className="quiz-secondary-btn"
                                            type="button"
                                            onClick={() => sendAction({
                                                action: "resolve_question_report",
                                                id: report.id
                                            })}
                                        >
                                            {text.markReportResolved}
                                        </button>
                                    )}
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}
                </section>
            )}
        </main>
    );
}
